import FileDownloadIcon from "@mui/icons-material/FileDownload"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material"
import { DataGrid, GridSortModel } from "@mui/x-data-grid"
import { TFunction } from "i18next"
import { isUndefined, map } from "lodash"
import moment from "moment"
import React, { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Locker } from "shared/locker"
import { LockerThingTypeMrn } from "shared/lockerData"
import { Sl2DataGridColumn } from "shared/report"
import { ReportDataset } from "shared/report-datasets"
import { Reservation } from "shared/reservation"
import { SharedAccessPatternOsEnum } from "shared/sharedAccessPatternsData"
import { SstDoor } from "shared/sst"
import { selectDatasetByName } from "src/features/report/reportSlice"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import {
  selectThingById,
  selectThingsByThingTypeMrn,
} from "src/features/things/thingsSlice"
import { getLockerDisplayValues } from "src/hooks/useLockerDisplayValues"
import useReportDatasets from "src/hooks/useReportDatasets"
import { useAppSelector } from "../app/hooks"
import { selectMeIsReady, selectTenantId } from "../features/me/meSlice"
import { parseSst2Utc } from "../sst2DateUtils"
import OsLockerDoorDrawer from "./OsLockerDoorDrawer"
import { OsReportBasicFilters } from "./OsReportBasicFilters"
import OsReportingPayment from "./OsReportingPayment"
import RectangularChip from "./RectangularChip"

const fieldToSortKeyMap: Record<string, string> = {
  number: "r.sst_number",
  status: "r.sst_status",
  locker: "r.locker",
  firstName: "r.first_name",
  lastName: "r.last_name",
  email: "r.email",
  phone: "r.phone",
  employeeCode: "r.employee_code",
  amountToCollect: "r.amount_to_collect",
  paymentCollectedAmount: "r.sst_payment_collected_amount",
  paymentMethod: "r.payment_method",
  createdAt: "r.created_at",
  deliveryTime: "r.sst_delivery_time",
  receptionTime: "r.sst_reception_time",
  doorLabel: "r.sst_door_label",
  doorSize: "r.sst_door_size",
}

const momentFormat = "YYYY-MM-DD HH:mm"

function formatReportTimestamp(value: string | null | undefined): string | null {
  const d = parseSst2Utc(value)
  return d ? moment(d).format(momentFormat) : null
}

const defaultColWidth = 200
type OsReportingReservationsProps = {
  onRefresh?: () => void
  reportType?: string
  datasetName?: string
  paymentDatasetOnly?: boolean
}
export const OsReportingReservations: FC<OsReportingReservationsProps> = ({
  reportType = "ReservationList2",
  datasetName = "reservationList2",
  paymentDatasetOnly = false,
}) => {
  const { t } = useTranslation()

  const tenantId = useAppSelector(selectTenantId)

  const isReady = useAppSelector(selectMeIsReady)

  const reservations = useAppSelector(
    selectDatasetByName(datasetName),
  ) as ReportDataset<any>

  const lockers = useAppSelector((s) =>
    selectThingsByThingTypeMrn(s, LockerThingTypeMrn),
  ) as Locker[]

  const [selectedReservationId, setSelectedReservationId] = useState<
    string | undefined
  >()

  const qReservation = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternOsEnum.GetReservationById,
        "ap.tenantId": tenantId,
        "ap.id": selectedReservationId,
        tenantId,
      },
    },
    {
      skip: !tenantId || !selectedReservationId,
    },
  )

  const reservationThing = useAppSelector((s) =>
    selectThingById(s, selectedReservationId || ""),
  ) as Reservation | undefined

  const locker = useAppSelector((s) =>
    selectThingById(s, reservationThing?.lockerId || ""),
  ) as Locker | undefined

  const door = useMemo(() => {
    const sstDoorId =
      reservationThing?.sstReservation?.door?._id ||
      reservationThing?.sstReservation?.door?.door_id;
    if (!sstDoorId) return undefined;

    // V1 locker: match by door_id in sstLocker.doors
    const v1Door = locker?.sstLocker?.doors?.find(
      (d: SstDoor) => d.door_id === sstDoorId,
    );
    if (v1Door) return v1Door;

    // V2 locker: match by id in sst2Locker.doors, then map to SstDoor shape for drawer compat
    const v2Door = locker?.sst2Locker?.doors?.find(
      (d: any) => d.id === sstDoorId,
    );
    if (v2Door) {
      // Resolve door size from locker template cells
      let doorSize = "";
      for (const ta of locker?.sst2Locker?.templates_association ?? []) {
        const dc = ta.template?.cells?.filter((c: any) => c.cell_type?.toUpperCase() === "DOOR") ?? [];
        if (v2Door.door_number >= 0 && v2Door.door_number < dc.length) {
          doorSize = dc[v2Door.door_number].size ?? "";
          break;
        }
      }
      return {
        door_id: v2Door.id,
        door_number: v2Door.door_number,
        door_label: `${v2Door.door_number}`,
        size: doorSize,
        size_description: "",
        locker_id: locker?.sstLockerId || "",
      } as SstDoor;
    }

    return undefined;
  }, [locker, reservationThing])

  const {
    refresh: refreshDatasets,
    fetchExportDownload,
    isFetchingExport,
    exportTruncation,
    isLoading: isLoading1,
    setPagination,
    pagination,
    setSortModel,
    error,
  } = useReportDatasets(reportType)

  const columns = useCallback(
    (t: TFunction): Sl2DataGridColumn[] => [
      // reservation.sstReservation.number
      {
        field: "number",
        headerName: t("number"),
        width: 100,
        renderCell: (params: any) => {
          const no = params.row?.reservation?.sst_number
          if (!no) {
            return <Box>-</Box>
          }
          return (
            <Stack
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
              spacing={1}
              height="100%"
            >
              <Link
                onClick={() =>
                  setSelectedReservationId(params.row?.reservation?.id)
                }
              >
                <Typography variant="body2" fontFamily={"monospace"}>
                  {no}
                </Typography>
              </Link>
            </Stack>
          )
        },
      },
      {
        field: "status",
        headerName: t("status"),
        width: 150,

        renderCell: (params: any) => {
          const label = params.row?.reservation?.sst_status
          if (!label) {
            return <Box>-</Box>
          }
          return (
            <Box>
              <RectangularChip
                label={t(`sstReservationStatus.${label}`)}
                variant="outlined"
                size="small"
              />
            </Box>
          )
        },
      },
      {
        field: "locker",
        headerName: t("locker"),
        sortable: false,
        width: defaultColWidth,
        renderCell: (params: any) => {
          const l = getLockerDisplayValues(params.row?.locker as Locker)

          return (
            <Box>
              <Chip
                label={
                  <Typography variant="caption">
                    {"🗄️ " + l.i18n.name}
                  </Typography>
                }
                variant="outlined"
              />
            </Box>
          )
        },
      },
      {
        field: "firstName",
        headerName: t("firstName"),
        width: defaultColWidth,
        renderCell: (params: any) => {
          const label = params.row?.reservation.first_name
          if (!label) {
            return <Box>-</Box>
          }
          return <Box>{label}</Box>
        },
      },
      // lastName
      {
        field: "lastName",
        headerName: t("lastName"),
        width: defaultColWidth,
        renderCell: (params: any) => {
          const label = params.row?.reservation.last_name
          if (!label) {
            return <Box>-</Box>
          }
          return <Box>{label}</Box>
        },
      },
      // email
      {
        field: "email",
        headerName: t("email"),
        width: defaultColWidth,
        renderCell: (params: any) => {
          const label = params.row?.reservation.email
          if (!label) {
            return <Box>-</Box>
          }
          return <Box>{label}</Box>
        },
      },
      // phone
      {
        field: "phone",
        headerName: t("phone"),
        width: defaultColWidth,
        renderCell: (params: any) => {
          const label = params.row?.reservation.phone
          if (!label) {
            return <Box>-</Box>
          }
          return <Box>{label}</Box>
        },
      },
      // employee_code
      {
        field: "employeeCode",
        headerName: t("employeeCode"),
        width: defaultColWidth,
        renderCell: (params: any) => {
          const label = params.row?.reservation.employee_code
          if (!label) {
            return <Box>-</Box>
          }
          return (
            <Box>
              <Chip label={label} variant="outlined" />
            </Box>
          )
        },
      },

      //amountToCollect
      {
        field: "amountToCollect",
        headerName: t("amountToCollect"),
        width: defaultColWidth,
        renderCell: (params: any) => {
          const label = params.row?.reservation.amount_to_collect
          if (!label) {
            return <Box>-</Box>
          }
          return <Box>{label}</Box>
        },
      },
      {
        field: "paymentMethod",
        headerName: t("paymentMethod"),
        width: defaultColWidth,
        renderCell: (params: any) => {
          const label = params.row?.reservation.payment_method
          if (!label) {
            return <Box>-</Box>
          }
          return <Box>{label}</Box>
        },
      },
      {
        field: "paymentCollectedAmount",
        headerName: t("paymentCollectedAmount"),
        width: defaultColWidth,
        renderCell: (params: any) => {
          const label = params.row?.reservation.sst_payment_collected_amount
          if (!label) {
            return <Box>-</Box>
          }
          return <Box>{label}</Box>
        },
      },
      {
        field: "createdAt",
        headerName: t("creationReservation"),
        width: defaultColWidth,
        renderCell: (params: any) => {
          const formatted = formatReportTimestamp(
            params.row.reservation.sst_created as string,
          )
          return <Box>{formatted ?? "-"}</Box>
        },
      },
      {
        field: "deliveryTime",
        headerName: t("deliveryTime"),
        width: defaultColWidth,
        renderCell: (params: any) => {
          const formatted = formatReportTimestamp(
            params.row?.reservation.sst_delivery_time as string,
          )
          return <Box>{formatted ?? "-"}</Box>
        },
      },

      {
        field: "receptionTime",
        headerName: t("receptionTime"),
        width: defaultColWidth,
        renderCell: (params: any) => {
          const formatted = formatReportTimestamp(
            params.row?.reservation.sst_reception_time as string,
          )
          return <Box>{formatted ?? "-"}</Box>
        },
      },
      {
        field: "doorLabel",
        headerName: t("doorLabel"),
        width: defaultColWidth,
        renderCell: (params: any) => {
          const doorNumber = params.row?.reservation?.sst_door_number

          const label = doorNumber ? `${doorNumber + 1}` : ""
          if (!label) {
            return <Box>-</Box>
          }
          return (
            <Box>
              <Chip label={`${t("door")} ${label}`} variant="outlined" />
            </Box>
          )
        },
      },
      {
        field: "doorSize",
        headerName: t("doorSize"),
        width: defaultColWidth,
        renderCell: (params: any) => {
          const label = params.row?.reservation.sst_door_size
          if (!label) {
            return <Box>-</Box>
          }
          return (
            <Box>
              <Chip label={`${t("door")} ${label}`} variant="outlined" />
            </Box>
          )
        },
      },
    ],
    [],
  )

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel[0]) {
        const sl2Field = fieldToSortKeyMap[sortModel[0].field as string] || ""
        setSortModel({
          field: sl2Field || sortModel[0].field,
          sort: sortModel[0].sort,
        })
      }
    },
    [setSortModel],
  )
  const [localFilters, setLocalFilters] = useState<any>({
    paymentDatasetOnly,
  })

  const onPaginationModelChange = useCallback(
    (paginationModel: any) => {
      setPagination(paginationModel)
      refreshDatasets({
        ...localFilters,
        pagination: { ...paginationModel, page: paginationModel.page + 1 },
      })
    },
    [localFilters, refreshDatasets, setPagination],
  )

  const { isLoading: isLoadingLockers } = useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternOsEnum.GetLockers,
        "ap.tenantId": tenantId,
        "ap.refreshSstLockers": true,
        tenantId,
      },
    },
    {
      skip: !tenantId,
    },
  )

  const isLoading = useMemo(
    () =>
      isLoadingLockers ||
      qReservation.isLoading ||
      qReservation.isFetching ||
      isLoading1,
    [
      isLoading1,
      isLoadingLockers,
      qReservation.isFetching,
      qReservation.isLoading,
    ],
  )

  const reservationTuples = useMemo(() => {
    return map(reservations.data, (reservation: any) => {
      const locker = lockers.find(
        (locker) => locker.id === reservation.locker_id,
      )
      return {
        id: reservation?.sst_id,
        sstReservation: reservation.sstReservation,
        locker,
        door: locker?.sstLocker?.doors.find(
          (door: SstDoor) => door.door_id === reservation.door_id,
        ),
        reservation,
      }
    }).filter(Boolean)
  }, [reservations.data, lockers])

  const [exportType, setExportType] = useState("")
  const [openDialog, setOpenDialog] = useState(false)
  const [exportOption, setExportOption] = useState("anonymized")

  const handleExportChange = useCallback((event: any) => {
    const type = event?.target?.value
    setExportType(type)
    if (type) {
      setOpenDialog(true)
    }
  }, [])

  const handleDialogClose = useCallback(() => {
    setOpenDialog(false)
    if (exportOption) {
      fetchExportDownload(
        exportType,
        localFilters,
        exportOption === "anonymized",
      )
    }
  }, [exportType, exportOption, fetchExportDownload, localFilters])

  const handleCancel = useCallback(() => {
    setOpenDialog(false)
  }, [])

  const handleRadioChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setExportOption((event.target as HTMLInputElement).value)
    },
    [],
  )

  return (
    <div data-ui="UI-SL2W-093" style={{ display: "contents" }}>
      {!isReady && (
        <>
          <Stack direction="column" spacing={2}>
            <Skeleton variant="rectangular" width="100%" height={118} />
            <Skeleton variant="rectangular" width="100%" height={118 / 2} />
            <Skeleton variant="rectangular" width="100%" height={118} />
            <Skeleton variant="rectangular" width="100%" height={118 / 2} />
          </Stack>
        </>
      )}
      {isReady && tenantId && (
        <Box>
          <Stack direction="column" spacing={3} maxWidth={"700px"}>
            <OsReportBasicFilters
              isLoading={isLoading1}
              onChange={({ filters } = {} as any) => {
                setLocalFilters({ ...filters, paymentDatasetOnly })
                refreshDatasets({ ...filters, paymentDatasetOnly })
              }}
            />

            {error && (
              <Box>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}
            {exportTruncation && (
              <Box>
                <Alert severity="info" data-testid="report-export-truncated">
                  {t("reportExportTruncated", exportTruncation)}
                </Alert>
              </Box>
            )}
            {paymentDatasetOnly && <OsReportingPayment isLoading={isLoading} />}
          </Stack>
          <Box pt={2} />
          <Stack direction="row" spacing={2} alignItems="center" pb={2}>
            {!isLoading && (
              <Typography variant="h6">{`${t("reservations")} (${
                reservations?.data?.length
              } / ${
                isUndefined(reservations?.totalCount)
                  ? "?"
                  : reservations?.totalCount
              })`}</Typography>
            )}
            {isLoading && (
              <Typography variant="h6">{`${t("reservations")}`}</Typography>
            )}
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                {/* <IconButton onClick={refetch}>
                  <Refresh />
                </IconButton> */}
                <Box>
                  <Select
                    value={""}
                    onChange={handleExportChange}
                    displayEmpty
                    variant="outlined"
                    size="small"
                    disabled={isFetchingExport}
                  >
                    <MenuItem value="" disabled>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <FileDownloadIcon />
                        <Typography variant="body2">{t("export")}</Typography>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="csv">
                      <Typography variant="body2">{t("exportCsv")}</Typography>
                    </MenuItem>
                    <MenuItem value="xlsx">
                      <Typography variant="body2">{t("exportXlsx")}</Typography>
                    </MenuItem>
                    <MenuItem value="pdf">
                      <Typography variant="body2">{t("exportPdf")}</Typography>
                    </MenuItem>
                  </Select>
                </Box>
                {isFetchingExport && <CircularProgress size={24} />}
              </Stack>
            )}
          </Stack>
          <DataGrid
            columns={columns(t)}
            rows={reservationTuples}
            localeText={{
              noRowsLabel: isLoading ? "Loading ..." : t("noReservations"),
            }}
            autoHeight
            getRowId={(row) => row.reservation?.id}
            loading={isLoading1}
            rowSelection={false}
            sx={{
              // disable cell selection style
              ".MuiDataGrid-cell:focus": {
                outline: "none",
              },
              // pointer cursor on ALL rows
              "& .MuiDataGrid-row:hover": {
                cursor: "pointer",
              },
            }}
            rowCount={
              reservations.totalCount || `${reservations?.data?.length}`
            }
            pageSizeOptions={[10, 25, 50, 100]}
            paginationModel={pagination}
            onPaginationModelChange={onPaginationModelChange}
            paginationMode="server"
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            disableColumnFilter
            disableColumnSorting
          />
          {reservationThing?.id && locker?.id && door && (
            <OsLockerDoorDrawer
              modName={t("module")}
              onClose={() => setSelectedReservationId(undefined)}
              reservation={reservationThing}
              locker={locker}
              isCreateMode={false}
              door={door}
            />
          )}
        </Box>
      )}
      <Dialog data-ui="UI-SL2W-094" open={openDialog} onClose={handleCancel}>
        <DialogTitle>{t("exportOptions")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("chooseExportOption")}</DialogContentText>
          <RadioGroup
            aria-label="export-options"
            name="export-options"
            value={exportOption}
            onChange={handleRadioChange}
          >
            <FormControlLabel
              value="anonymized"
              control={<Radio />}
              label={t("anonymizedData")}
            />
            <FormControlLabel
              value="full"
              control={<Radio />}
              label={t("fullData")}
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>{t("cancel")}</Button>
          <Button onClick={handleDialogClose} variant="contained">
            {t("export")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default OsReportingReservations
